import type { CSSProperties, ReactNode } from 'react';

// Hairline outline + design-system shadow-md. The outline + shadow-md combo is what gives the
// card its "Formwerk" inset rim look — see CardShadowOverlay below for the second half.
const CARD_SHADOW: CSSProperties = {
    boxShadow: '0 0 0 1px var(--color-gray-300), var(--shadow-md)',
};
// Inset highlights from `var(--shadow-md)` are normally hidden behind child elements that
// have their own background colour (header `bg-gray-200`, body `bg-white-200`). Painting the
// same shadow on an absolutely positioned overlay re-applies the rim ON TOP of children so
// the bezel reads cleanly.
const CARD_OVERLAY_SHADOW: CSSProperties = { boxShadow: 'var(--shadow-md)' };

export type ChangeProposalCardProps = {
    readonly children: ReactNode;
    readonly className?: string;
};

export type ChangeProposalCardHeaderProps = {
    readonly label: string;
    readonly action?: ReactNode;
};

export type ChangeProposalCardBodyProps = {
    readonly children: ReactNode;
    readonly className?: string;
};

export type ChangeProposalCardActionsProps = {
    readonly children?: ReactNode;
    readonly className?: string;
};

function CardShadowOverlay() {
    return (
        <div
            aria-hidden={true}
            className="pointer-events-none absolute inset-0 rounded-[inherit]"
            style={CARD_OVERLAY_SHADOW}
        />
    );
}

function Header({ label, action }: ChangeProposalCardHeaderProps) {
    return (
        <div className="flex items-center justify-between gap-m bg-gray-200 p-l">
            <span className="text-xs font-medium text-black-600">{label}</span>
            {action}
        </div>
    );
}

function Body({ children, className }: ChangeProposalCardBodyProps) {
    return <div className={`bg-white-200 p-l ${className ?? ''}`}>{children}</div>;
}

function Text({ children }: { readonly children: ReactNode }) {
    return (
        <div className="max-h-[min(280px,45vh)] overflow-y-auto whitespace-pre-wrap break-words text-sm leading-[1.5] text-black-800">
            {children}
        </div>
    );
}

function TaskList({ tasks }: { readonly tasks: readonly string[] }) {
    return (
        <ul className="m-0 max-h-[min(280px,45vh)] list-disc space-y-s overflow-y-auto pl-xl marker:text-black-500">
            {tasks.map((task) => (
                <li key={task} className="break-words pl-xxs text-sm leading-[1.5] text-black-800">
                    {task}
                </li>
            ))}
        </ul>
    );
}

export type ChangeProposalCardTitleChangeProps = {
    readonly current?: string;
    readonly proposed: string;
};

function TitleChange({ current, proposed }: ChangeProposalCardTitleChangeProps) {
    return (
        <div className="flex max-h-[min(280px,45vh)] flex-col gap-xs overflow-y-auto">
            {current !== undefined && current.length > 0 ? (
                <div className="whitespace-pre-wrap break-words text-sm leading-snug tracking-tight text-gray-600 line-through">
                    {current}
                </div>
            ) : null}
            <div className="whitespace-pre-wrap break-words text-lg font-medium leading-snug tracking-tight text-black-800">
                {proposed}
            </div>
        </div>
    );
}

export type ChangeProposalCardSkillEntryProps = {
    readonly title: string;
    readonly subtitle?: string;
    readonly struck?: boolean;
};

function SkillEntry({ title, subtitle, struck }: ChangeProposalCardSkillEntryProps) {
    return (
        <div className="flex items-start gap-s py-1">
            <div className="min-w-0 flex-1">
                <div
                    className={`break-words text-sm font-medium leading-snug tracking-tight ${
                        struck ? 'text-black-400 line-through' : 'text-black-900'
                    }`}
                >
                    {title}
                </div>
                {subtitle !== undefined && subtitle.length > 0 ? (
                    <div className="mt-0.5 break-words text-xs leading-snug text-black-400">{subtitle}</div>
                ) : null}
            </div>
        </div>
    );
}

function Actions({ children, className }: ChangeProposalCardActionsProps) {
    return (
        <div className={`flex flex-col gap-s border-t border-gray-200 bg-white-200 p-l ${className ?? ''}`}>
            {children}
        </div>
    );
}

export function ChangeProposalCard({ children, className }: ChangeProposalCardProps) {
    return (
        <div className={`relative w-full overflow-hidden rounded-xxl font-sans ${className ?? ''}`} style={CARD_SHADOW}>
            {children}
            <CardShadowOverlay />
        </div>
    );
}

ChangeProposalCard.Header = Header;
ChangeProposalCard.Body = Body;
ChangeProposalCard.Actions = Actions;
ChangeProposalCard.Text = Text;
ChangeProposalCard.TaskList = TaskList;
ChangeProposalCard.TitleChange = TitleChange;
ChangeProposalCard.SkillEntry = SkillEntry;
